import { BikeType, RidingStyle, RidingExperience, Flexibility } from '../constants/bikeConfig';
import {
  ANGLE_REFERENCE_RANGES,
  HARD_LIMITS,
  BORDERLINE_BUFFER,
  EXPERIENCE_MODIFIERS,
  FLEXIBILITY_MODIFIERS,
  AngleKey,
  AngleStatus,
} from '../constants/angleReferenceRanges';
import { AngleData } from '../modules/ProcessingPipeline';

export interface AngleRange {
  min: number;
  max: number;
}

export interface AngleAssessment {
  status: AngleStatus;
  range: AngleRange | null;
  /** Positive = above ideal max, negative = below ideal min, 0 = within range */
  deviation: number;
}

export interface AngleColorPair {
  arc: string;
  text: string;
}

const STATUS_COLORS: Record<Exclude<AngleStatus, 'no_range'>, AngleColorPair> = {
  good:            { arc: 'rgba(16, 185, 129, 0.95)', text: '#10B981' },
  borderline:      { arc: 'rgba(245, 158, 11, 0.95)', text: '#f59e0b' },
  needs_attention: { arc: 'rgba(239, 68, 68, 0.95)',  text: '#ef4444' },
};

// Fallback type-based colors (matches canvasDrawing.ts ANGLE_COLORS)
const TYPE_COLORS: Record<string, AngleColorPair> = {
  knee:  { arc: 'rgba(255, 215, 0, 0.95)',   text: '#FFD700' },
  hip:   { arc: 'rgba(255, 120, 40, 0.95)',   text: '#FF7828' },
  trunk: { arc: 'rgba(100, 210, 255, 0.95)',  text: '#64D2FF' },
  elbow: { arc: 'rgba(120, 255, 120, 0.95)',  text: '#78FF78' },
};

/**
 * Look up the base range for a given bike type, riding style, and angle key.
 * Returns null if the combination is not valid.
 */
export function getAngleRange(
  bikeType: BikeType,
  ridingStyle: RidingStyle,
  angleKey: AngleKey
): AngleRange | null {
  const styleRanges = ANGLE_REFERENCE_RANGES[bikeType];
  if (!styleRanges) return null;

  const styleEntry = styleRanges[ridingStyle];
  if (!styleEntry) return null;

  const range = styleEntry[angleKey];
  if (!range) return null;

  return range;
}

/**
 * Compute the adjusted angle range using the v2.0 4-step pipeline:
 *   1. Base range (bike type × riding style)
 *   2. Experience modifier (shifts entire window)
 *   3. Flexibility modifier (shifts min only, for hip_tdc and trunk)
 *   4. Hard limits clamp the result
 *
 * Returns null if no base range exists for the combination.
 */
export function getAdjustedRange(
  bikeType: BikeType,
  ridingStyle: RidingStyle,
  angleKey: AngleKey,
  experience: RidingExperience,
  flexibility: Flexibility
): AngleRange | null {
  const base = getAngleRange(bikeType, ridingStyle, angleKey);
  if (!base) return null;

  // Step 2 — experience modifier
  const expMod = EXPERIENCE_MODIFIERS[experience][angleKey];
  let adjMin = base.min + expMod.min_shift;
  let adjMax = base.max + expMod.max_shift;

  // Step 3 — flexibility modifier (min only)
  const flexMod = FLEXIBILITY_MODIFIERS[flexibility][angleKey];
  adjMin = adjMin + flexMod.min_shift;
  // adjMax is unchanged by flexibility

  // Step 4 — enforce hard limits
  const hard = HARD_LIMITS[angleKey];
  if (hard) {
    if (hard.absolute_min !== undefined) adjMin = Math.max(adjMin, hard.absolute_min);
    if (hard.absolute_max !== undefined) adjMax = Math.min(adjMax, hard.absolute_max);
  }

  return { min: adjMin, max: adjMax };
}

/**
 * Assess a single measured angle against the v2.0 adjusted reference range.
 */
export function assessAngle(
  measured: number,
  bikeType: BikeType,
  ridingStyle: RidingStyle,
  angleKey: AngleKey,
  experience: RidingExperience = 'intermediate',
  flexibility: Flexibility = 'good'
): AngleAssessment {
  const range = getAdjustedRange(bikeType, ridingStyle, angleKey, experience, flexibility);

  if (!range) {
    return { status: 'no_range', range: null, deviation: 0 };
  }

  // Hard limit override — always red regardless of adjusted range
  const hard = HARD_LIMITS[angleKey];
  if (hard) {
    if (hard.absolute_min !== undefined && measured < hard.absolute_min) {
      return { status: 'needs_attention', range, deviation: measured - hard.absolute_min };
    }
    if (hard.absolute_max !== undefined && measured > hard.absolute_max) {
      return { status: 'needs_attention', range, deviation: measured - hard.absolute_max };
    }
  }

  if (measured >= range.min && measured <= range.max) {
    return { status: 'good', range, deviation: 0 };
  }

  const deviationBelow = range.min - measured;
  const deviationAbove = measured - range.max;
  const deviation = deviationAbove > 0 ? deviationAbove : -deviationBelow;

  const buffer = BORDERLINE_BUFFER;
  if (measured >= range.min - buffer && measured <= range.max + buffer) {
    return { status: 'borderline', range, deviation };
  }

  return { status: 'needs_attention', range, deviation };
}

export interface PerPositionAssessments {
  knee: AngleAssessment;
  hip: AngleAssessment;
  trunk: AngleAssessment;
  elbow: AngleAssessment;
}

export interface BikeFitAssessment {
  /** Assessments based on BDC angles — knee and elbow/trunk are primary here */
  bdc: PerPositionAssessments;
  /** Assessments based on TDC angles — hip and elbow/trunk are primary here */
  tdc: PerPositionAssessments;
  /** Trunk assessed against the average value */
  trunkAvg: AngleAssessment;
}

/**
 * Run a full assessment for both pedal positions using the v2.0 pipeline.
 *
 * - Knee is evaluated at BDC (primary); at TDC it returns 'no_range'.
 * - Hip is evaluated at TDC (primary); at BDC it returns 'no_range'.
 * - Trunk uses the passed average value for both positions.
 * - Elbow is position-independent — same result for both.
 */
export function assessBikeFit(
  bikeType: BikeType,
  ridingStyle: RidingStyle,
  bdcAngles: AngleData,
  tdcAngles: AngleData,
  trunkAvg: number | null,
  experience: RidingExperience = 'intermediate',
  flexibility: Flexibility = 'good'
): BikeFitAssessment {
  const trunkValue = trunkAvg ?? bdcAngles.trunk;

  const assess = (measured: number, key: AngleKey) =>
    assessAngle(measured, bikeType, ridingStyle, key, experience, flexibility);

  const noRange: AngleAssessment = { status: 'no_range', range: null, deviation: 0 };

  const bdcAssessments: PerPositionAssessments = {
    knee:  assess(bdcAngles.knee,  'knee_bdc'),
    hip:   noRange,
    trunk: assess(trunkValue,       'trunk'),
    elbow: assess(bdcAngles.elbow, 'elbow'),
  };

  const tdcAssessments: PerPositionAssessments = {
    knee:  noRange,
    hip:   assess(tdcAngles.hip,   'hip_tdc'),
    trunk: assess(trunkValue,       'trunk'),
    elbow: assess(tdcAngles.elbow, 'elbow'),
  };

  return {
    bdc: bdcAssessments,
    tdc: tdcAssessments,
    trunkAvg: assess(trunkValue, 'trunk'),
  };
}

/**
 * Resolve the canvas arc/text color for an angle given its assessment.
 * Falls back to the type-based color when no reference range exists.
 */
export function statusToColor(
  assessment: AngleAssessment,
  angleType: 'knee' | 'hip' | 'trunk' | 'elbow'
): AngleColorPair {
  if (assessment.status === 'no_range') {
    return TYPE_COLORS[angleType];
  }
  return STATUS_COLORS[assessment.status as Exclude<AngleStatus, 'no_range'>];
}
